import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import Heading from '@pelckmans/elementis/components/heading';
import { addDays, startOfDay } from 'date-fns';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group } from '../../../../../../api/groups';
import useStudioGroup from '../../../../../../hooks/useStudioGroup';
import LocalizedDateField from '../../../../../../shared-components/elementis/components/localized-date-field';
import { Status } from '../../../../components/GroupListItem.code-based';
import { SchoolYear } from '../../../../hooks/useGroups';
import { FormValues } from '../EditGroupModal';

export interface Props {
  group: Group;
  selectedSchoolyear: SchoolYear;
  nextSchoolYear?: {
    from: Date;
    to: Date;
  };
}

function getSchoolYearLimitEndDate(schoolYear: string, schoolYears: { from: Date; to: Date }[]): Date | undefined {
  const [startYear] = schoolYear.split('-').map(Number);
  return schoolYears.find(({ from }) => from.getFullYear() === startYear + 1)?.to;
}

function getEarlierDate(a?: Date, b?: Date): Date | undefined {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}

export default function EditGroupAvailability({ group, selectedSchoolyear, nextSchoolYear }: Props) {
  const { t } = useTranslation();
  const minDate = addDays(startOfDay(new Date()), 1);
  const studioGroup = useStudioGroup();
  const { schoolYears } = useSchoolYear();
  const schoolYearLimitEndDate = getSchoolYearLimitEndDate(group.schoolYear, schoolYears);
  const maxDate = getEarlierDate(schoolYearLimitEndDate, nextSchoolYear?.to);

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const status = useWatch({ control, name: 'status' });
  const startDateValue = useWatch({ control, name: 'startDate' });
  const isPublished = status === 'published';
  const hideControls = status === 'closed' && selectedSchoolyear.hasPassed;

  if (studioGroup === 'BAO') return null;

  return (
    <>
      <Heading level={4} className="availability-header modal-content-text-title">
        {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.HEADING')}
      </Heading>

      <Status group={group} />
      <div className="availability-form">
        {!hideControls && (
          <>
            <Controller
              name="status"
              control={control}
              defaultValue={group.status ?? 'published'}
              render={({ field }) => (
                <Checkbox
                  checked={field.value === 'published'}
                  onChange={e => {
                    if (group.status === 'closed' || group.status === 'unpublished') {
                      setValue('startDate', startOfDay(new Date()).toISOString());
                      setValue('endDate', '');
                    }
                    field.onChange(e.target.checked ? 'published' : 'unpublished');
                  }}
                  disabled={group.status === 'published'}
                >
                  {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.PUBLISH_TOGGLE')}
                </Checkbox>
              )}
            />

            <div data-testid="start-date-container">
              <LocalizedDateField
                labelText={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.START_DATE.LABEL')}
                mode="single"
                selected={startDateValue ? new Date(startDateValue) : undefined}
                onSelect={() => undefined}
                required
                disabled
                id="start-date"
              />
            </div>
            <Controller
              control={control}
              name="endDate"
              rules={{
                required: isPublished && t('FORM.VALIDATION.REQUIRED'),
                validate: (value: string) =>
                  !value ||
                  !schoolYearLimitEndDate ||
                  new Date(value) <= schoolYearLimitEndDate ||
                  t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.END_DATE.ERROR_MAX_SCHOOL_YEARS'),
              }}
              render={({ field }) => (
                <div data-testid="end-date-container">
                  <LocalizedDateField
                    labelText={
                      status === 'unpublished'
                        ? t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.END_DATE.UNPUBLISHED.LABEL')
                        : t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.END_DATE.PUBLISHED.LABEL')
                    }
                    id="end-date"
                    mode="single"
                    selected={field.value ? new Date(field.value) : undefined}
                    onSelect={(date: Date | undefined) => {
                      date && field.onChange(date.toISOString());
                      field.onBlur();
                    }}
                    required
                    minDate={minDate}
                    maxDate={maxDate}
                    error={!!errors.endDate}
                    errorText={errors.endDate?.message as string}
                    disabled={!isPublished}
                    helpText={
                      isPublished ? t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.END_DATE.HELP_TEXT') : undefined
                    }
                  />
                </div>
              )}
            />
          </>
        )}
      </div>
    </>
  );
}
